/**
 * TemplateFieldBuilderWidget — DnD field builder for Item Customization Template.
 *
 * Reads fields from formData child table, renders draggable FieldCards,
 * opens FieldSheet for add/edit, writes back via onFieldChange("fields", newArray).
 *
 * Plan: 126-template-detail-page, Epic 02
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import { Plus, Layers } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { FieldCard } from "../components/FieldCard";
import { FieldSheet } from "../components/FieldSheet";
import type { TemplateField } from "../api/customizationAdminTypes";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { SectionHeader } from "@/features/inventory/item-detail/components/SectionHeader";
import { useLanguage } from "@/i18n/useLanguage";
import { getSectionTitleTranslation } from "@/lib/views/labels/translations";

const i18n = {
  addField: { en: "Add Field", th: "เพิ่มฟิลด์" },
  addFirstField: { en: "Add your first field", th: "เพิ่มฟิลด์แรกของคุณ" },
  fieldsHelp: {
    en: "Fields are what your customers fill in — text, dropdowns, toggles, or file uploads.",
    th: "ฟิลด์คือสิ่งที่ลูกค้าของคุณกรอก — ข้อความ ตัวเลือก สวิตช์ หรืออัปโหลดไฟล์",
  },
} as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Assign sort_order (0-based) and idx (1-based, Frappe convention) to every row. */
function withSortOrder<T extends Record<string, unknown>>(rows: T[]): T[] {
  return rows.map((row, i) => ({ ...row, sort_order: i, idx: i + 1 }));
}

/** Generate a stable local ID for unsaved rows (stripped by prepareDocForSave). */
function makeLocalId(): string {
  return `__new_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

// ---------------------------------------------------------------------------
// SortableFieldCard — wraps FieldCard with @dnd-kit sortable behavior
// ---------------------------------------------------------------------------

function SortableFieldCard({
  field,
  onEdit,
  onDelete,
}: {
  field: TemplateField & { _localId?: string };
  onEdit: () => void;
  onDelete: () => void;
}) {
  const id = field.name || field._localId || "";
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <FieldCard
        field={field}
        onEdit={onEdit}
        onDelete={onDelete}
        dragHandleProps={listeners}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main widget
// ---------------------------------------------------------------------------

type FieldRow = TemplateField & { _localId?: string; [key: string]: unknown };

export function TemplateFieldBuilderWidget({
  formData,
  onFieldChange,
  resetKey,
}: ExperienceWidgetProps) {
  const { language } = useLanguage();
  const t = (key: keyof typeof i18n) => i18n[key][language] || i18n[key].en;

  const fields = (formData.fields || []) as FieldRow[];

  // -- Local UI state --
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Reset local state when StandardDetailPage saves (resetKey changes)
  useEffect(() => {
    setSheetOpen(false);
    setEditingIndex(null);
  }, [resetKey]);

  // -- DnD sensors --
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Sortable IDs: use name for saved rows, _localId for new rows. Never label.
  const sortableIds = useMemo(
    () => fields.map((f) => f.name || f._localId || ""),
    [fields],
  );

  // -- Computed --
  const editField = useMemo(
    () => (editingIndex !== null ? fields[editingIndex] ?? null : null),
    [editingIndex, fields],
  );

  // -- Handlers --

  const handleAddField = useCallback(() => {
    setEditingIndex(null);
    setSheetOpen(true);
  }, []);

  const handleEditField = useCallback((index: number) => {
    setEditingIndex(index);
    setSheetOpen(true);
  }, []);

  const handleFieldSave = useCallback(
    (fieldData: Omit<TemplateField, "name" | "sort_order">) => {
      let updated: FieldRow[];

      if (editingIndex !== null) {
        const existing = fields[editingIndex];
        updated = fields.map((f, i) =>
          i === editingIndex ? { ...existing, ...fieldData } : f,
        );
        setSheetOpen(false);
        setEditingIndex(null);
      } else {
        const newRow: FieldRow = {
          ...fieldData,
          sort_order: fields.length,
          _localId: makeLocalId(),
        };
        updated = [...fields, newRow];
      }

      onFieldChange("fields", withSortOrder(updated));
    },
    [editingIndex, fields, onFieldChange],
  );

  const handleDeleteField = useCallback(
    (index: number) => {
      const updated = fields.filter((_, i) => i !== index);
      onFieldChange("fields", withSortOrder(updated));
    },
    [fields, onFieldChange],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = fields.findIndex(
        (f) => (f.name || f._localId) === active.id,
      );
      const newIndex = fields.findIndex(
        (f) => (f.name || f._localId) === over.id,
      );
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(fields, oldIndex, newIndex);
      onFieldChange("fields", withSortOrder(reordered));
    },
    [fields, onFieldChange],
  );

  // -- Render --

  return (
    <div>
      {/* Section header row with action button on the right */}
      <div className="flex items-end justify-between mb-3">
        <SectionHeader label={getSectionTitleTranslation("Fields", language)} icon={Layers} color="teal" />
        {fields.length > 0 && (
          <Button variant="outline" size="sm" onClick={handleAddField} className="shrink-0 mb-0.5">
            <Plus className="h-4 w-4 mr-1" />
            {t("addField")}
          </Button>
        )}
      </div>

      <div className="rounded-2xl overflow-hidden bg-card shadow-apple p-6">
        {/* Empty state */}
        {fields.length === 0 ? (
          <div className="border-2 border-dashed border-muted-foreground/20 rounded p-8 text-center text-muted-foreground">
            <Layers className="h-8 w-8 mx-auto mb-3 opacity-40" />
            <p className="font-medium">{t("addFirstField")}</p>
            <p className="text-sm mt-1">{t("fieldsHelp")}</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={handleAddField}>
              <Plus className="h-4 w-4 mr-1" />
              {t("addField")}
            </Button>
          </div>
        ) : (
          /* Field cards with DnD */
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortableIds}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {fields.map((field, index) => (
                  <SortableFieldCard
                    key={field.name || field._localId || `field-${index}`}
                    field={field}
                    onEdit={() => handleEditField(index)}
                    onDelete={() => handleDeleteField(index)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        {/* Field sheet (add/edit) */}
        <FieldSheet
          open={sheetOpen}
          onOpenChange={(open) => {
            setSheetOpen(open);
            if (!open) setEditingIndex(null);
          }}
          editField={editField}
          onSave={handleFieldSave}
        />
      </div>
    </div>
  );
}
